/**
 * Generates varied alt and title attributes for profile images
 * to improve SEO and avoid repetition
 * Variations optimized for SEO with keywords like sexy, hot, exclusif, leak, fan, etc.
 * Separated into VIDEO and PHOTO variations
 */

const VIDEO_VARIATIONS = [
  "{name}",
  "{name} video sexy",
  "{name} video hot",
  "{name} videos sexy",
  "{name} videos hot",
  "{name} video exclusif",
  "{name} videos exclusives",
  "{name} videos illimitees",
  "{name} videos et photos",
  "{name} contenu exclusif",
  "{name} contenu illimite",
  "{name} contenus exclusifs",
  "{name} contenus illimites",
  "{name} fan",
  "{name} fans",
  "{name} leak",
  "{name} leaks",
  "{name}video sexy",
  "{name}videos sexy",
  "{name}videos exclusives",
  "{name}contenu exclusif",
  "{name}contenus exclusifs",
  "{name}contenu illimite",
  "{name}contenus illimites",
  "{name}videosillimitees",
  "video sexy {name}",
  "videos sexy {name}",
  "video hot {name}",
  "videos hot {name}",
  "videos exclusives {name}",
  "videos illimitees {name}",
  "contenu exclusif {name}",
  "contenus exclusifs {name}",
  "contenu illimite {name}",
  "contenus illimites {name}",
  "leak {name}",
  "leaks {name}",
  "fan {name}",
  "fans {name}",
  "videos et photos {name}",
  "{name} fan club",
  "{name} fans club",
  "{name} video sexy leak",
  "{name} videos hot leaks",
  "{name} videos sexy exclusives",
  "{name} contenu hot",
  "{name} contenus hot",
  "{name} video hot",
  "{name}videoshot",
  "{name}contenuhot",
  "{name}contenusexclusifs",
  "{name}leak",
  "{name}leaks",
  "{name}fan",
  "{name}fans",
  "videoexclusif {name}",
  "videosexclusives {name}",
  "contenusexclusifs {name}",
  "contenuhot {name}",
  "videoshot {name}",
  "fanclub {name}",
  "fansclub {name}",
  "{name} videos photos",
  "{name} videos exclusif",
  "{name} contenu sexy",
  "{name} contenus sexy",
  "{name} fans sexy",
  "{name} fan hot",
  "videos illimitees {name}",
  "contenu sexy {name}",
  "contenus sexy {name}",
  "video exclusif {name}",
  "fans sexy {name}",
  "fan hot {name}",
  "leaks sexy {name}",
  "contenu exclusif hot {name}",
];

const PHOTO_VARIATIONS = [
  "{name}",
  "{name} photo sexy",
  "{name} photo hot",
  "{name} photos sexy",
  "{name} photos hot",
  "{name} photo exclusif",
  "{name} photos exclusives",
  "{name} photos illimitees",
  "{name} photos et videos",
  "{name} contenu exclusif",
  "{name} contenu illimite",
  "{name} contenus exclusifs",
  "{name} contenus illimites",
  "{name} fan",
  "{name} fans",
  "{name} leak",
  "{name} leaks",
  "{name}photo hot",
  "{name}photos hot",
  "{name}photos exclusives",
  "{name}contenu exclusif",
  "{name}contenus exclusifs",
  "{name}contenu illimite",
  "{name}contenus illimites",
  "{name}photosillimitees",
  "photo sexy {name}",
  "photos sexy {name}",
  "photo hot {name}",
  "photos hot {name}",
  "photos exclusives {name}",
  "photos illimitees {name}",
  "contenu exclusif {name}",
  "contenus exclusifs {name}",
  "contenu illimite {name}",
  "contenus illimites {name}",
  "leak {name}",
  "leaks {name}",
  "fan {name}",
  "fans {name}",
  "photos et videos {name}",
  "{name} fan club",
  "{name} fans club",
  "{name} photos sexy exclusives",
  "{name} contenu hot",
  "{name} contenus hot",
  "{name} photo hot",
  "{name}photossexy",
  "{name}contenuhot",
  "{name}contenusexclusifs",
  "{name}leak",
  "{name}leaks",
  "{name}fan",
  "{name}fans",
  "photoexclusif {name}",
  "photosexclusives {name}",
  "contenusexclusifs {name}",
  "contenuhot {name}",
  "photossexy {name}",
  "fanclub {name}",
  "fansclub {name}",
  "{name} photos videos",
  "{name} photos exclusif",
  "{name} contenu sexy",
  "{name} contenus sexy",
  "{name} fans sexy",
  "{name} fan hot",
  "photos illimitees {name}",
  "contenu sexy {name}",
  "contenus sexy {name}",
  "photo exclusif {name}",
  "fans sexy {name}",
  "fan hot {name}",
  "leaks sexy {name}",
  "contenu exclusif hot {name}",
];

interface GenerateAltTextParams {
  name?: string;
  username?: string;
  index?: number;
  id?: string;
  fallback?: string;
  isVideo?: boolean;
}

/**
 * Generates a varied alt/title text based on the item's index or ID
 * @param params - Parameters used to generate the text
 * @returns The generated alt/title text
 */
export const generateImageAltText = ({
  name,
  username,
  index = 0,
  id,
  fallback = "image",
  isVideo = false,
}: GenerateAltTextParams): string => {
  // If neither name nor username is provided, return the fallback
  if (!name && !username) {
    return fallback;
  }

  // Select the appropriate variations based on content type
  const variations = isVideo ? VIDEO_VARIATIONS : PHOTO_VARIATIONS;

  // Determine which variation index to use
  let variationIndex: number;
  
  if (id) {
    // Use a simple hash of the ID to determine the variation
    const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    variationIndex = hash % variations.length;
  } else {
    // Use the provided index
    variationIndex = index % variations.length;
  }

  // Get the variation template
  const template = variations[variationIndex];

  // Replace placeholders
  let result = template;
  if (name) {
    result = result.replace(/{name}/g, name);
  }
  if (username) {
    result = result.replace(/{username}/g, username);
  }

  // If placeholders have not been replaced, use the available fallback
  if (result.includes("{name}") && !name) {
    result = result.replace(/{name}/g, username || fallback);
  }
  if (result.includes("{username}") && !username) {
    result = result.replace(/{username}/g, name || fallback);
  }

  return result;
};
